import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Checkbox,
  CircularProgress,
  Typography,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Password as PasswordIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { User } from '../../../types/models';

export interface UserTableProps {
  users: User[];
  loading: boolean;
  error: { message?: string } | undefined;
  selectedUsers: string[];
  formatDate: (dateString: string | undefined) => string;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onPassword: (user: User) => void;
  onSelectUser: (userId: string) => void;
  onSelectAll: (checked: boolean) => void;
  permBulkUpdate?: boolean;
  permUpdate?: boolean;
  permDelete?: boolean;
  permChangePassword?: boolean;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  error,
  selectedUsers,
  formatDate,
  onEdit,
  onDelete,
  onPassword,
  onSelectUser,
  onSelectAll,
  permBulkUpdate = true,
  permUpdate = true,
  permDelete = true,
  permChangePassword = true,
}) => {
  const theme = useTheme();
  const selectableUsers = users.filter(u => u.userRole !== 'SYSTEM_ADMIN' && !u.locked);
  const selectableIds = selectableUsers.map(u => u.userId);
  const allSelected = selectableIds.length > 0 && selectedUsers.length === selectableIds.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < selectableIds.length;

  if (loading) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">
            データの読み込み中にエラーが発生しました。
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
      <TableContainer>
        <Table aria-label="ユーザー一覧">
          <TableHead sx={{ bgcolor: '#F0F7FF' }}>
            <TableRow>
              <TableCell padding="checkbox" sx={{ color: theme.palette.smartSkillTalk.linkBlue }}>
                {permBulkUpdate && (
                  <Checkbox
                    color="primary"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                )}
              </TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>管理者</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>氏名</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>ログインID</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>メールアドレス</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>所属</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>状態</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>最終ログイン</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk.linkBlue, fontSize: '13px' }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow
                key={user.userId}
                sx={{
                  bgcolor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                  '&:hover': { bgcolor: '#F5F5F5' }
                }}
              >
                <TableCell padding="checkbox">
                  {user.userRole !== 'SYSTEM_ADMIN' && !user.locked && permBulkUpdate && (
                    <Checkbox
                      checked={selectedUsers.includes(user.userId)}
                      onChange={() => onSelectUser(user.userId)}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  {user.userRole === 'ADMIN' && (
                    user.locked ? (
                      <Tooltip
                        title={
                          <span style={{ whiteSpace: 'pre-line' }}>
                            特権管理者は、削除や変更ができません<br />詳しくは「ご利用マニュアル」を確認ください
                          </span>
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            bgcolor: '#FF6B35',
                            color: 'white',
                            borderRadius: '2px',
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            fontSize: '10px',
                            cursor: 'help'
                          }}
                        >
                          特権管理者
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box
                        sx={{
                          bgcolor: '#FCA600',
                          color: 'white',
                          borderRadius: '2px',
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          fontSize: '10px'
                        }}
                      >
                        管理者
                      </Box>
                    )
                  )}
                  {user.userRole === 'SYSTEM_ADMIN' && (
                    <Box
                      sx={{
                        bgcolor: '#E91E63',
                        color: 'white',
                        borderRadius: '2px',
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        fontSize: '10px'
                      }}
                    >
                      システム管理者
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>{user.displayName}</TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>{user.givenUserId}</TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>{user.email}</TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                  {(() => {
                    const depts = user.departments && user.departments.length > 0
                      ? user.departments
                      : (user.department ? [user.department] : []);
                    if (depts.length === 0) return '-';
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                        {depts.map((dept) => (
                          <Chip key={dept} label={dept} size="small" sx={{ fontSize: '12px' }} />
                        ))}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>
                  {!user.disable ? (
                    <Tooltip title="有効">
                      <CheckCircleIcon
                        sx={{
                          color: '#4CAF50',
                          fontSize: '20px'
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="無効">
                      <BlockIcon
                        sx={{
                          color: '#F44336',
                          fontSize: '20px'
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}>{formatDate(user.lastLoginDate)}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    {user.userRole !== 'SYSTEM_ADMIN' && !user.locked && (
                      <>
                        {permUpdate && (
                          <Tooltip title="編集">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(user)}
                              sx={{
                                bgcolor: '#5D84AF',
                                color: 'white',
                                '&:hover': { bgcolor: '#5D84AF', opacity: 0.9 }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {permChangePassword && (
                          <Tooltip title="パスワード変更">
                            <IconButton
                              size="small"
                              onClick={() => onPassword(user)}
                              sx={{
                                bgcolor: '#FF9800',
                                color: 'white',
                                '&:hover': { bgcolor: '#FF9800', opacity: 0.9 }
                              }}
                            >
                              <PasswordIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {permDelete && (
                          <Tooltip title="削除">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(user)}
                              sx={{
                                bgcolor: '#F44336',
                                color: 'white',
                                '&:hover': { bgcolor: '#F44336', opacity: 0.9 }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default React.memo(UserTable);
